import { Component } from '@theme/component';

/**
 * @typedef {Object} AxKlaviyoCtaRefs
 * @property {HTMLButtonElement} trigger
 */

/**
 * Ax Klaviyo CTA — bouton qui ouvre un formulaire Klaviyo et remonte deux
 * événements de suivi (ouverture, soumission).
 *
 * L'app embed Klaviyo fournit `window._klOnsite` (file d'attente des commandes
 * onsite) et `window.klaviyo.track`. Aucun des deux n'est garanti présent : le
 * composant ne fait donc rien de bloquant si l'app n'est pas chargée, et pousser
 * dans `_klOnsite` avant l'init du script est justement le mode d'emploi prévu.
 *
 * @extends {Component<AxKlaviyoCtaRefs>}
 */
class AxKlaviyoCta extends Component {
  requiredRefs = ['trigger'];

  #onFormEvent = null;

  connectedCallback() {
    super.connectedCallback();

    const { formId } = this.dataset;
    if (!formId) return;

    this.#onFormEvent = (event) => this.#handleFormEvent(event, formId);
    window.addEventListener('klaviyoForms', this.#onFormEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#onFormEvent) {
      window.removeEventListener('klaviyoForms', this.#onFormEvent);
      this.#onFormEvent = null;
    }
  }

  /** Ouvre le formulaire Klaviyo. Câblé via `on:click` sur le bouton. */
  open() {
    const { formId } = this.dataset;
    if (!formId) return;

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  /**
   * @param {CustomEvent} event
   * @param {string} formId
   */
  #handleFormEvent(event, formId) {
    const detail = event.detail;
    if (!detail || detail.formId !== formId) return;

    const prefix = this.dataset.eventPrefix;
    if (!prefix) return;

    if (detail.type === 'open') {
      this.#track(`${prefix} - Ouverture Formulaire`, { formId });
      return;
    }

    // `stepSubmit` couvre les formulaires multi-étapes, où `submit` n'arrive
    // qu'à la toute fin (voire jamais si la dernière étape est un remerciement).
    if (detail.type === 'submit' || detail.type === 'stepSubmit') {
      this.#track(`${prefix} - Soumission Formulaire`, {
        formId,
        email: detail.metaData ? detail.metaData.email : null,
      });
    }
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} payload
   */
  #track(name, payload) {
    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.track !== 'function') return;

    klaviyo.track(name, { ...payload, timestamp: new Date().toISOString() });
  }
}

if (!customElements.get('ax-klaviyo-cta')) {
  customElements.define('ax-klaviyo-cta', AxKlaviyoCta);
}
